import React, { useState, useEffect, useMemo } from 'react';
import useAppStore from '../../store/useAppStore.js';
import { fetchBranches } from '../../services/branchApi.js';
import { fetchInventoryHistory, fetchProducts } from '../../services/inventoryApi.js';
import TabToggle from '../common/TabToggle.js';
import ReportCard from '../common/ReportCard.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';
import { getSessionStart } from '../../utils/dateHelpers.js';

const reportSections = [
  { label: 'Sales', value: 'sales' },
  { label: 'Expenses', value: 'expenses' },
  { label: 'Inventory', value: 'inventory' }
];

const formatCurrency = formatCurrencyShort;

const formatIsoDate = (date) => new Date(date).toISOString().split('T')[0];

const getDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return 'Select period';
  return `${new Date(startDate).toLocaleDateString()} – ${new Date(endDate).toLocaleDateString()}`;
};

const buildDateKey = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const buildMonthKey = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const getSessionDayKey = (timestamp) => {
  const time = new Date(timestamp);
  const sessionStart = getSessionStart(time, 15);
  return sessionStart.toISOString().split('T')[0];
};

const parseBranchId = (branchValue) => {
  if (!branchValue || branchValue === 'all') return null;
  const normalized = branchValue.toString().replace(/^BR-/, '');
  const numeric = Number(normalized);
  return Number.isNaN(numeric) ? null : numeric;
};

const AdminReports = () => {
  const {
    admin,
    loadSalesReport,
    loadExpenseReport,
    loadInventoryReport,
    clearReportErrors,
    loadUsers
  } = useAppStore((state) => ({
    admin: state.admin,
    loadSalesReport: state.admin.loadSalesReport,
    loadExpenseReport: state.admin.loadExpenseReport,
    loadInventoryReport: state.admin.loadInventoryReport,
    clearReportErrors: state.admin.clearReportErrors,
    loadUsers: state.admin.loadUsers
  }));

  const [activeSection, setActiveSection] = useState('sales');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return formatIsoDate(date);
  });
  const [endDate, setEndDate] = useState(formatIsoDate(new Date()));
  const [viewMode, setViewMode] = useState('breakdown');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedManagerBranch, setSelectedManagerBranch] = useState('all');
  const [managerDate, setManagerDate] = useState(formatIsoDate(new Date()));
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [products, setProducts] = useState([]);

  // Load reports on component mount and when section changes
  useEffect(() => {
    clearReportErrors();
    
    const loadReports = async () => {
      try {
        const [branchesResponse, historyResponse, productsResponse] = await Promise.all([
          fetchBranches(),
          fetchInventoryHistory(),
          fetchProducts()
        ]);

        setBranches(branchesResponse.branches || []);
        setInventoryHistory(historyResponse.history || []);
        setProducts(productsResponse.products || []);

        const branchId = parseBranchId(selectedBranch);

        // Load the current section's data
        if (activeSection === 'sales') {
          await loadSalesReport({ startDate, endDate, branchId });
        } else if (activeSection === 'expenses') {
          await loadExpenseReport({ startDate, endDate, branchId });
        } else if (activeSection === 'inventory') {
          await loadInventoryReport();
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };

    loadReports();
  }, [activeSection, startDate, endDate, selectedBranch, loadSalesReport, loadExpenseReport, loadInventoryReport, clearReportErrors]);

  const dateRangeValid = !!startDate && !!endDate && startDate <= endDate;
  const dateRangeLabel = getDateRangeLabel(startDate, endDate);
  const revenueTrendLabel = dateRangeValid ? 'Last 7 days' : 'Select period';

  useEffect(() => {
    if (!admin.usersLoaded && !admin.usersLoading) {
      loadUsers();
    }
  }, [admin.usersLoaded, admin.usersLoading, loadUsers]);

  // Reload report when the selected period changes
  useEffect(() => {
    const reloadWithDateRange = async () => {
      if (!dateRangeValid) return;
      const branchId = parseBranchId(selectedBranch);
      if (activeSection === 'sales') {
        await loadSalesReport({ startDate, endDate, branchId });
      } else if (activeSection === 'expenses') {
        await loadExpenseReport({ startDate, endDate, branchId });
      }
    };
    
    if (activeSection !== 'inventory') {
      reloadWithDateRange();
    }
  }, [startDate, endDate, activeSection, selectedBranch, dateRangeValid, loadSalesReport, loadExpenseReport]);

  // Extract data from store with proper defaults
  const sales = (admin?.reports?.sales?.data) || [];
  const expenses = (admin?.reports?.expenses?.data) || [];
  const inventory = ((admin?.reports?.inventory?.data) || []).map(item => ({
    id: item.id,
    name: item.name,
    category: item.category || 'Uncategorized',
    stock: parseFloat(item.currentStock) || 0,
    minStock: parseFloat(item.reorder_level) || 0,
    unit: item.unit || 'pcs',
    costPrice: parseFloat(item.cost_price) || 0,
    status: item.status || 'In Stock'
  }));

  // Check if any report is loading
  const loading = 
    admin?.reports?.sales?.loading || 
    admin?.reports?.expenses?.loading || 
    admin?.reports?.inventory?.loading ||
    false;

  // Get error from current section
  const error = activeSection === 'sales' 
    ? admin?.reports?.sales?.error 
    : activeSection === 'expenses'
    ? admin?.reports?.expenses?.error
    : admin?.reports?.inventory?.error || null;

  const filteredSales = useMemo(() => {
    const branchManagerMap = (admin?.users || []).reduce((acc, user) => {
      if (user && user.role === 'manager') {
        const branchId = parseBranchId(user.branchId);
        if (branchId != null && !acc[String(branchId)]) {
          acc[String(branchId)] = {
            id: String(user.id),
            name: user.name || user.email || 'Unknown Manager'
          };
        }
      }
      return acc;
    }, {});

    return (sales || []).map((sale) => {
      const branchId = parseBranchId(sale.branch_id);
      const manager = branchId != null ? branchManagerMap[String(branchId)] : null;

      return {
        ...sale,
        timestamp: sale.timestamp ? new Date(sale.timestamp) : new Date(),
        revenue: parseFloat(sale.revenue) || 0,
        discount: parseFloat(sale.discount) || 0,
        managerId: manager?.id || 'unknown',
        managerName: manager?.name || 'Unknown Manager'
      };
    });
  }, [sales, admin.users]);

  const managerOptions = useMemo(() => {
    const managers = (admin?.users || [])
      .filter((user) => user && user.role === 'manager')
      .map((user) => ({
        label: user.name || user.email || `Manager ${user.id}`,
        value: String(user.id)
      }));

    return [
      { label: 'All managers', value: 'all' },
      ...managers.sort((a, b) => a.label.localeCompare(b.label))
    ];
  }, [admin.users]);

  const salesByBranch = useMemo(() => {
    const branchMap = {};
    
    // Initialize branch map from branches array
    (branches || []).forEach((branch) => {
      const branchId = parseBranchId(branch.id);
      if (branch && branchId != null) {
        branchMap[String(branchId)] = {
          id: branch.id,
          name: branch.name || 'Unknown',
          location: branch.location || 'N/A',
          salesCount: 0,
          revenue: 0
        };
      }
    });

    // Aggregate sales data
    (filteredSales || []).forEach((sale) => {
      if (!sale) return;
      const branchId = parseBranchId(sale.branch_id);
      
      if (branchId != null && branchMap[String(branchId)]) {
        branchMap[String(branchId)].salesCount += 1;
        branchMap[String(branchId)].revenue += parseFloat(sale.revenue) || 0;
      }
    });

    return Object.values(branchMap).sort((a, b) => b.revenue - a.revenue);
  }, [branches, filteredSales]);

  const salesByProduct = useMemo(() => {
    const totals = {};
    
    (filteredSales || []).forEach((sale) => {
      if (!sale || !sale.items) return;
      
      sale.items.forEach((item) => {
        if (!item) return;
        const itemId = item.id || item.product_id;
        const itemName = item.name || item.product_name || 'Unknown';
        const quantity = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || parseFloat(item.unit_price) || 0;
        const itemRevenue = parseFloat(item.revenue) || price * quantity;
        
        if (!itemId) return;
        
        if (!totals[itemId]) {
          totals[itemId] = { 
            id: itemId, 
            name: itemName, 
            quantity: 0, 
            revenue: 0 
          };
        }
        
        totals[itemId].quantity += quantity;
        totals[itemId].revenue += itemRevenue;
      });
    });

    return Object.values(totals).sort((a, b) => b.quantity - a.quantity);
  }, [filteredSales]);

  const revenueTrend = useMemo(() => {
    if (!dateRangeValid) return [];

    const groupedByDate = {};
    (filteredSales || []).forEach((sale) => {
      const sessionKey = sale.timestamp ? getSessionDayKey(sale.timestamp) : formatIsoDate(new Date());
      groupedByDate[sessionKey] = (groupedByDate[sessionKey] || 0) + (sale.revenue || 0);
    });

    const end = new Date(`${endDate}T00:00:00`);
    const start = new Date(`${startDate}T00:00:00`);
    const totalDays = Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)) + 1);
    const trendDays = Math.min(totalDays, 7);
    const cursor = new Date(end);
    cursor.setDate(cursor.getDate() - (trendDays - 1));

    const data = [];
    for (let i = 0; i < trendDays; i += 1) {
      const isoDate = formatIsoDate(cursor);
      data.push({
        key: buildDateKey(cursor),
        date: new Date(cursor),
        revenue: groupedByDate[isoDate] || 0
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return data;
  }, [filteredSales, dateRangeValid, startDate, endDate]);

  const totalSalesRevenue = useMemo(
    () => admin.reports.sales.totals?.revenue || 0,
    [admin.reports.sales.totals]
  );

  const totalOrders = useMemo(
    () => admin.reports.sales.totals?.count || 0,
    [admin.reports.sales.totals]
  );

  const salesByManager = useMemo(() => {
    const managerBranchId = parseBranchId(selectedManagerBranch);
    
    if (!managerDate) {
      return [];
    }

    const managerMap = {};
    const targetDateStr = managerDate;

    (filteredSales || []).forEach((sale) => {
      // Apply branch filter if set
      if (managerBranchId != null) {
        const saleBranchId = parseBranchId(sale.branch_id);
        if (saleBranchId !== managerBranchId) return;
      }

      // Use business_date from sales data if available, otherwise use formatted date
      const saleBusinessDate = sale.business_date || formatIsoDate(sale.timestamp || new Date());
      
      // Only include sales from the selected date
      if (saleBusinessDate !== targetDateStr) return;

      const managerId = sale.managerId || 'unknown';
      const managerName = sale.managerName || 'Unknown Manager';

      if (!managerMap[managerId]) {
        managerMap[managerId] = {
          managerId,
          managerName,
          revenue: 0,
          discount: 0,
          sold: 0
        };
      }

      managerMap[managerId].revenue += sale.revenue || 0;
      managerMap[managerId].discount += sale.discount || 0;
      managerMap[managerId].sold += (sale.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    });

    // Convert to array and apply manager filter
    const result = Object.values(managerMap)
      .filter((manager) => {
        const managerMatch = selectedManager && selectedManager !== 'all'
          ? manager.managerId === selectedManager
          : true;
        return managerMatch;
      })
      .sort((a, b) => b.revenue - a.revenue);

    return result;
  }, [filteredSales, selectedManager, selectedManagerBranch, managerDate]);

  const salesByPaymentMethod = useMemo(() => {
    const paymentMethods = {};
    
    (filteredSales || []).forEach((sale) => {
      if (!sale) return;
      const method = sale.payment_method || 'cash';
      
      if (!paymentMethods[method]) {
        paymentMethods[method] = {
          method: method.charAt(0).toUpperCase() + method.slice(1),
          originalMethod: method,
          count: 0,
          revenue: 0
        };
      }
      
      paymentMethods[method].count += 1;
      paymentMethods[method].revenue += sale.revenue || 0;
    });

    return Object.values(paymentMethods).sort((a, b) => b.revenue - a.revenue);
  }, [filteredSales]);

  const expenseTrend = useMemo(() => {
    if (!dateRangeValid) return [];

    const groupedByDate = {};
    (expenses || []).forEach((expense) => {
      const isoDate = expense.date ? formatIsoDate(expense.date) : formatIsoDate(new Date());
      groupedByDate[isoDate] = (groupedByDate[isoDate] || 0) + (expense.amount || 0);
    });

    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const data = [];
    const cursor = new Date(start);

    while (cursor <= end) {
      const isoDate = formatIsoDate(cursor);
      data.push({
        key: buildDateKey(cursor),
        date: new Date(cursor),
        amount: groupedByDate[isoDate] || 0
      });
      cursor.setDate(cursor.getDate() + 1);
    }

    return data;
  }, [expenses, dateRangeValid, startDate, endDate]);

  const lowStockItems = useMemo(
    () => inventory.filter((item) => item.stock <= item.minStock),
    [inventory]
  );

  const topSellingItems = useMemo(
    () => salesByProduct.slice(0, 5),
    [salesByProduct]
  );

  const expenseSummary = useMemo(() => {
    const summary = admin.reports.expenses.summary || { total: 0, byCategory: {}, count: 0 };
    const byCategory = Object.entries(summary.byCategory || {}).sort((a, b) => b[1] - a[1]);
    
    // Build month breakdown from expenses data
    const byMonth = {};
    (expenses || []).forEach((exp) => {
      const monthKey = buildMonthKey(exp.date || new Date());
      byMonth[monthKey] = (byMonth[monthKey] || 0) + (exp.amount || 0);
    });
    
    const byMonthArray = Object.entries(byMonth).sort((a, b) => new Date(a[0]) - new Date(b[0]));

    return {
      total: summary.total || 0,
      byCategory: byCategory,
      byMonth: byMonthArray
    };
  }, [admin.reports.expenses.summary, expenses]);

  const consumptionTrend = useMemo(() => {
    if (!inventoryHistory || inventoryHistory.length === 0) {
      return [];
    }
    
    const recentHistory = inventoryHistory.slice(0, 14) || [];
    const monthlyTrend = recentHistory.reduce((acc, event) => {
      if (!event || !event.timestamp) return acc;
      const month = buildMonthKey(event.timestamp);
      const change = parseFloat(event.quantityChange) || 0;
      acc[month] = (acc[month] || 0) + Math.abs(change);
      return acc;
    }, {});

    return Object.entries(monthlyTrend)
      .map(([month, consumption]) => ({ month, consumption }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [inventoryHistory]);

  const renderTrendChart = (data, labelKey, valueKey, accent = 'bg-blue-600') => {
    const maxValue = Math.max(...data.map((item) => item[valueKey]), 1);
    return (
      <div className="space-y-3">
        {data.map((item) => (
          <div key={item[labelKey]} className="space-y-1">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{item[labelKey]}</span>
              <span>{formatCurrency(item[valueKey])}</span>
            </div>
            <div className="h-3 rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full ${accent}`}
                style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-3/5 rounded-full bg-gray-200" />
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-28 rounded-3xl bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Admin Reports</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Deep analytics for sales, expenses, and inventory across the entire branch network.
          </p>
        </div>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
              <span className="mb-2 block font-semibold">From</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
              />
            </label>
            <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
              <span className="mb-2 block font-semibold">To</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
              />
            </label>
            <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
              <span className="mb-2 block font-semibold">Branch</span>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
              >
                <option value="all">All branches</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </label>
          </div>
          <TabToggle options={reportSections} activeValue={activeSection} onChange={setActiveSection} />
        </div>
      </div>

      {error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {activeSection === 'sales' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <ReportCard title="Total revenue" value={formatCurrency(totalSalesRevenue)} subtitle="Revenue for selected date range" />
            <ReportCard title="Total orders" value={totalOrders} subtitle="Sales transactions in scope" />
            <ReportCard title="Branches reporting" value={salesByBranch.filter((branch) => branch.salesCount > 0).length} subtitle="Branches with sales activity" />
            <ReportCard title="Products sold" value={salesByProduct.reduce((sum, item) => sum + item.quantity, 0)} subtitle="Units sold across products" />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Revenue trends</h2>
                  <p className="mt-1 text-sm text-gray-500">Daily revenue performance for the last week.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {revenueTrendLabel}
                </span>
              </div>
              <div className="mt-6 h-[400px] overflow-auto">{renderTrendChart(revenueTrend, 'key', 'revenue')}</div>
            </section>

            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Top selling items</h2>
                  <p className="mt-1 text-sm text-gray-500">Most popular products in the selected range.</p>
                </div>
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700">Top {topSellingItems.length}</span>
              </div>
              <div className="mt-6 space-y-3 h-[400px] overflow-auto">
                {topSellingItems.map((product) => (
                  <div key={product.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-900">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.quantity} sold</div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">{formatCurrency(product.revenue)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Manager sales summary</h2>
                <p className="mt-1 text-sm text-gray-500">Date-wise revenue, discount, and sold quantity by manager.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                {managerDate ? new Date(managerDate).toLocaleDateString() : 'Select date'}
              </span>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-4">
              <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
                <span className="mb-2 block font-semibold">Date</span>
                <input
                  type="date"
                  value={managerDate}
                  onChange={(e) => setManagerDate(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
                />
              </label>
              <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
                <span className="mb-2 block font-semibold">Branch</span>
                <select
                  value={selectedManagerBranch}
                  onChange={(e) => setSelectedManagerBranch(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
                >
                  <option value="all">All branches</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </label>
              <label className="rounded-3xl border border-gray-200 bg-white p-4 text-sm text-slate-700">
                <span className="mb-2 block font-semibold">Manager</span>
                <select
                  value={selectedManager}
                  onChange={(e) => setSelectedManager(e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900"
                >
                  {managerOptions.map((manager) => (
                    <option key={manager.value} value={manager.value}>{manager.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-6 overflow-auto h-[400px]">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">Manager</th>
                    <th className="px-4 py-3 font-medium">Revenue</th>
                    <th className="px-4 py-3 font-medium">Discount</th>
                    <th className="px-4 py-3 font-medium">Sold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesByManager.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">No manager sales found for the selected filters.</td>
                    </tr>
                  ) : (
                    salesByManager.map((manager) => (
                      <tr key={manager.managerId} className="hover:bg-gray-50">
                        <td className="px-4 py-4 font-medium text-slate-900">{manager.managerName}</td>
                        <td className="px-4 py-4 text-gray-900">{formatCurrency(manager.revenue)}</td>
                        <td className="px-4 py-4 text-gray-600">{formatCurrency(manager.discount)}</td>
                        <td className="px-4 py-4 text-gray-600">{manager.sold}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Sales by payment method</h2>
                <p className="mt-1 text-sm text-gray-500">Revenue and transaction count by payment method.</p>
              </div>
              <span className="rounded-full bg-purple-50 px-3 py-1 text-sm font-semibold text-purple-700">{dateRangeLabel}</span>
            </div>
            <div className="mt-6 overflow-auto h-[400px]">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">Payment Method</th>
                    <th className="px-4 py-3 font-medium">Transactions</th>
                    <th className="px-4 py-3 font-medium">Revenue</th>
                    <th className="px-4 py-3 font-medium">Avg per Transaction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesByPaymentMethod.map((method) => (
                    <tr key={method.originalMethod} className="hover:bg-gray-50">
                      <td className="px-4 py-4 font-medium text-slate-900">{method.method}</td>
                      <td className="px-4 py-4 text-gray-600">{method.count}</td>
                      <td className="px-4 py-4 text-gray-900">{formatCurrency(method.revenue)}</td>
                      <td className="px-4 py-4 text-gray-600">{formatCurrency(method.revenue / method.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeSection === 'expenses' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <ReportCard title="Total expenses" value={formatCurrency(expenseSummary.total)} subtitle="Expense amount tracked" />
            <ReportCard title="Expense categories" value={expenseSummary.byCategory.length} subtitle="Distinct categories" />
            <ReportCard title="Recent entries" value={expenses.length} subtitle="Expenses recorded" />
            <ReportCard title="Breakdown view" value={viewMode === 'breakdown' ? 'Details' : 'Summary'} subtitle="Toggle report mode" />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Expenses by category</h2>
                  <p className="mt-1 text-sm text-gray-500">See where expenses are concentrated.</p>
                </div>
                <TabToggle
                  options={[
                    { label: 'Breakdown', value: 'breakdown' },
                    { label: 'Summary', value: 'summary' }
                  ]}
                  activeValue={viewMode}
                  onChange={setViewMode}
                />
              </div>
              <div className="mt-6 space-y-3 h-[400px] overflow-auto">
                {viewMode === 'breakdown' ? (
                  expenseSummary.byCategory.map(([category, amount]) => (
                    <div key={category} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between gap-3 text-sm text-gray-700">
                        <span>{category}</span>
                        <span>{formatCurrency(amount)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-700">Total spent across all categories</div>
                    <div className="mt-4 text-3xl font-semibold text-slate-900">{formatCurrency(expenseSummary.total)}</div>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Expense by date</h2>
                  <p className="mt-1 text-sm text-gray-500">Daily expense totals for the selected date range.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {dateRangeValid ? dateRangeLabel : 'Invalid range'}
                </span>
              </div>
              <div className="mt-6 h-[400px] overflow-auto">{renderTrendChart(expenseTrend, 'key', 'amount', 'bg-rose-600')}</div>
            </section>
          </div>

          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Expense entries</h2>
                <p className="mt-1 text-sm text-gray-500">Latest expenses and category details.</p>
              </div>
            </div>
            <div className="mt-6 h-[400px] overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {expenses.map((expenseItem) => (
                    <tr key={expenseItem.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-gray-800">{expenseItem.description}</td>
                      <td className="px-4 py-4 text-gray-600">{expenseItem.category}</td>
                      <td className="px-4 py-4 text-gray-900">{formatCurrency(expenseItem.amount)}</td>
                      <td className="px-4 py-4 text-gray-600">{new Date(expenseItem.date).toLocaleDateString()}</td>
                      <td className="px-4 py-4 text-gray-600">{expenseItem.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeSection === 'inventory' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <ReportCard title="Inventory items" value={inventory.length} subtitle="Ingredients and packaging" />
            <ReportCard title="Low stock alerts" value={lowStockItems.length} subtitle="Items needing restock" badge={lowStockItems.length > 0 ? 'Action needed' : 'Healthy'} />
            <ReportCard title="Tracked adjustments" value={inventoryHistory.length} subtitle="Recent inventory events" />
            <ReportCard title="Last month consumption" value={formatCurrency(consumptionTrend.reduce((sum, item) => sum + item.consumption, 0))} subtitle="Stock movement" />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Low stock alerts</h2>
                <p className="mt-1 text-sm text-gray-500">Items that are at or below minimum thresholds.</p>
              </div>
              <div className="mt-6 h-[300px] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-3 font-medium">Item</th>
                      <th className="px-4 py-3 font-medium">Stock</th>
                      <th className="px-4 py-3 font-medium">Min stock</th>
                      <th className="px-4 py-3 font-medium">Category</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {lowStockItems.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">All items are above minimum stock levels.</td>
                      </tr>
                    ) : (
                      lowStockItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-gray-800">{item.name}</td>
                          <td className="px-4 py-4 text-gray-900">{item.stock}</td>
                          <td className="px-4 py-4 text-gray-600">{item.minStock}</td>
                          <td className="px-4 py-4 text-gray-600">{item.category}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Consumption trends</h2>
                <p className="mt-1 text-sm text-gray-500">Inventory adjustments over recent months.</p>
              </div>
              <div className="mt-6 h-[300px] overflow-auto">
                {consumptionTrend.length ? (
                  consumptionTrend.map((point) => (
                    <div key={point.month} className="mb-4">
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{point.month}</span>
                        <span>{formatCurrency(point.consumption)}</span>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-slate-900"
                          style={{ width: `${Math.min((point.consumption / Math.max(...consumptionTrend.map((item) => item.consumption), 1)) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500">No consumption history available yet.</div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
